const API_BASE = '/api'

function getToken(): string | null {
  return localStorage.getItem('pmp_token')
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  if (res.status === 401) {
    const errBody = await res.json().catch(() => ({ error: 'session_expired' }))
    const reason = errBody.error || 'session_expired'

    // Distinguish portal-session 401s (session_expired, invalid token,
    // role missing) from GCP-token 401s (google_*). Only the former
    // should kick the user out to /login — the GCP-token codes are
    // request-scoped:
    //
    //   google_token_missing   — transient OAuth-refresh race during
    //                            attachGoogleToken. The user's portal
    //                            session is fine; the very next request
    //                            usually succeeds because the refresh
    //                            has settled. Logging them out here
    //                            would be a brutal UX regression on
    //                            what is effectively a cold-start blip.
    //
    //   google_reauth_required — refresh token genuinely revoked / RAPT
    //                            expired. Still don't auto-redirect:
    //                            the calling component shows the error
    //                            inline (artifact badge, preview
    //                            banner) and the operator can choose to
    //                            sign out from the user menu. Auto-
    //                            redirecting in the middle of a wizard
    //                            destroys their in-progress work.
    //
    // For both google_* cases we let the error propagate to the caller
    // with the structured detail/hint in the message, same as any other
    // 4xx response.
    const isGcpTokenError =
      reason === 'google_token_missing' || reason === 'google_reauth_required'

    if (!isGcpTokenError) {
      localStorage.removeItem('pmp_token')
      window.location.href = `/login?reason=${reason}`
      throw new Error('Unauthorized')
    }
    // Fall through to the generic !res.ok branch below so the caller
    // sees a proper Error with structured detail.
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    // Compose a more informative message: backend routes return either
    // `{ error: '...' }` (short code/summary) or `{ error, detail }` (e.g.
    // adopt-drift collisions, reauth-required). Surface `detail` in
    // the thrown Error.message so simple `error.message` UI bindings
    // (ProvisionerPartnersView, AppDetailView) show the real cause.
    // The full body is still attached as `.data` for callers that
    // want structured access.
    const detail = typeof err.detail === 'string' ? err.detail : null
    const summary = typeof err.error === 'string' ? err.error : 'Request failed'
    const message = detail ? `${summary}: ${detail}` : summary
    throw Object.assign(new Error(message), { status: res.status, data: err })
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

export const api = {
  // Version info (no auth required)
  version: () => request<{
    sha: string;
    buildTime: string | null;
    nodeEnv: string;
    uptime: number;
    commits: { sha: string; message: string; author: string; date: string }[];
  }>('/version'),

  // Auth
  auth: {
    me: () => request('/auth/me'),
    verify: () => request('/auth/verify', { method: 'POST' }),
    logout: () => request('/auth/logout', { method: 'POST' }),
    localLogin: (email: string, password: string) =>
      request('/auth/local', { method: 'POST', body: JSON.stringify({ email, password }) }),
    sessionInfo: () => request('/auth/session-info'),
    extendSession: () => request('/auth/extend-session', { method: 'POST' }),
  },

  // Users
  users: {
    list: () => request('/users'),
    get: (id: number) => request(`/users/${id}`),
    create: (data: unknown) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: unknown) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/users/${id}`, { method: 'DELETE' }),
    pending: () => request('/users/pending'),
    approve: (id: number) => request(`/users/${id}/approve`, { method: 'POST' }),
    reject: (id: number) => request(`/users/${id}/reject`, { method: 'POST' }),
  },

  // Partners
  partners: {
    list: (params?: Record<string, string>) => {
      const q = params ? '?' + new URLSearchParams(params).toString() : ''
      return request(`/partners${q}`)
    },
    unified: (params?: { status?: string; environment?: string; source?: string; realtime?: string; scope?: string; project?: string; q?: string }) => {
      const sp = new URLSearchParams()
      if (params?.status) sp.set('status', params.status)
      if (params?.environment) sp.set('environment', params.environment)
      if (params?.source) sp.set('source', params.source)
      if (params?.realtime) sp.set('realtime', params.realtime)
      if (params?.scope) sp.set('scope', params.scope)
      if (params?.project) sp.set('project', params.project)
      if (params?.q) sp.set('q', params.q)
      const q = sp.toString()
      return request<import('../types').UnifiedPartnersResponse>(`/partners/unified${q ? `?${q}` : ''}`)
    },
    get: (id: number) => request(`/partners/${id}`),
    create: (data: unknown) => request('/partners', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: unknown) => request(`/partners/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/partners/${id}`, { method: 'DELETE' }),
    members: {
      list: (id: number) => request(`/partners/${id}/members`),
      add: (id: number, data: unknown) => request(`/partners/${id}/members`, { method: 'POST', body: JSON.stringify(data) }),
      remove: (id: number, userId: number) => request(`/partners/${id}/members/${userId}`, { method: 'DELETE' }),
    },
    approvers: {
      list: (id: number) => request(`/partners/${id}/approvers`),
      add: (id: number, data: unknown) => request(`/partners/${id}/approvers`, { method: 'POST', body: JSON.stringify(data) }),
      remove: (id: number, userId: number) => request(`/partners/${id}/approvers/${userId}`, { method: 'DELETE' }),
    },
    environments: {
      list: (id: number) => request(`/partners/${id}/environments`),
      update: (id: number, envId: number, data: unknown) =>
        request(`/partners/${id}/environments/${envId}`, { method: 'PUT', body: JSON.stringify(data) }),
      sync: (id: number, envId: number) =>
        request(`/partners/${id}/environments/${envId}/sync`, { method: 'POST' }),
    },
    metrics: {
      summary: (environmentId?: number, days = 7) => {
        const params = new URLSearchParams({ days: String(days) })
        if (environmentId) params.set('environment_id', String(environmentId))
        return request(`/partners/metrics/summary?${params}`)
      },
    },
  },

  // Environments
  environments: {
    list: () => request('/environments'),
    get: (id: number) => request(`/environments/${id}`),
    create: (data: unknown) => request('/environments', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: unknown) => request(`/environments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/environments/${id}`, { method: 'DELETE' }),
    partnerHealth: (envId: number) => request(`/environments/${envId}/partner-health`),
  },

  // Dataflow
  //
  // All endpoints are jobName-based, not jobId-based. Rationale: the
  // Datastore `clientJobId` field is only refreshed by the CF on
  // first-provision and on the upgrade path, so it goes stale on any
  // other deploy (manual relaunch, parallel _v2, drained-then-restarted)
  // and points at a GC'd job. The deterministic CF jobName
  // (`[sb-]nile-realtime-<clientId>-<commit7>[_v2]`) is reconstructible
  // from the entity's commitHash + isSandBox + clientId, so the
  // backend resolves jobName→jobId live via Dataflow ListJobs(filter=
  // ACTIVE) on every call. See backend/src/routes/dataflow.ts for the
  // server side.
  dataflow: {
    /** Per-job snapshot: state, startTime, configuredWorkers, plus
     *  parsed scalar metrics (currentVcpu — live capacity from
     *  CurrentVcpuCount; messagesPushed — cumulative ElementCount on
     *  the `EgvPush/POST to url-out0` step, i.e. messages
     *  successfully delivered to the partner endpoint). One Dataflow
     *  API call resolves the jobName, a second pulls GetMetrics.
     *  Used by the Streaming Status card. */
    /** Cloud Monitoring time-series for the job. metric: short alias
     *  ('element_count' | 'elapsed_time' | 'system_lag' |
     *   'data_watermark_age' | 'backlog_bytes' | 'backlog_seconds' |
     *   'cpu_utilization') or a full dataflow.googleapis.com metric.
     *  Returns ALIGN_RATE-aligned points for cumulative metrics, raw
     *  points otherwise. */
    timeseries: (projectId: string, region: string, jobName: string, metric?: string, hours?: number) => {
      const params = new URLSearchParams()
      if (metric) params.set('metric', metric)
      if (hours) params.set('hours', String(hours))
      const qs = params.toString()
      return request(`/dataflow/jobs/${encodeURIComponent(projectId)}/${encodeURIComponent(region)}/${encodeURIComponent(jobName)}/timeseries${qs ? '?' + qs : ''}`)
    },
  },

  // Change requests
  changeRequests: {
    list: (params?: Record<string, string>) => {
      const q = params ? '?' + new URLSearchParams(params).toString() : ''
      return request(`/change-requests${q}`)
    },
    pendingCount: () => request('/change-requests/pending/count'),
    get: (id: number) => request(`/change-requests/${id}`),
    create: (data: unknown) => request('/change-requests', { method: 'POST', body: JSON.stringify(data) }),
    review: (id: number, data: unknown) =>
      request(`/change-requests/${id}/review`, { method: 'POST', body: JSON.stringify(data) }),
    cancel: (id: number) => request(`/change-requests/${id}/cancel`, { method: 'POST' }),
  },

  // API Keys
  apiKeys: {
    list: () => request('/api-keys'),
    create: (data: unknown) => request('/api-keys', { method: 'POST', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/api-keys/${id}`, { method: 'DELETE' }),
  },

  // Audit logs
  auditLogs: {
    list: (params?: Record<string, string>) => {
      const q = params ? '?' + new URLSearchParams(params).toString() : ''
      return request(`/audit-logs${q}`)
    },
    myActivity: (limit?: number) => {
      const q = limit ? `?limit=${limit}` : ''
      return request<{ logs: import('../types').AuditLog[] }>(`/audit-logs/my-activity${q}`)
    },
  },

  // Settings
  settings: {
    get: () => request('/settings'),
    getPublic: () => request('/settings/public'),
    update: (data: unknown) => request('/settings', { method: 'PUT', body: JSON.stringify(data) }),
  },

  // AI
  ai: {
    status: () => request('/ai/status'),
    conversations: {
      list: () => request('/ai/conversations'),
      get: (id: number) => request(`/ai/conversations/${id}`),
      delete: (id: number) => request(`/ai/conversations/${id}`, { method: 'DELETE' }),
    },
    // Chat is done via SSE, handled separately
  },

  // Developer mode
  developerMode: {
    request: (reason: string) => request('/developer-mode/request', { method: 'POST', body: JSON.stringify({ reason }) }),
  },

  // Apps (OAuth client registrations per partner + environment)
  apps: {
    list: (params?: Record<string, string>) => {
      const q = params ? '?' + new URLSearchParams(params).toString() : ''
      return request(`/apps${q}`)
    },
    get: (id: number) => request(`/apps/${id}`),
    resolve: (clientId: string) => request<import('../types').UnifiedAppDetail>(`/apps/resolve/${encodeURIComponent(clientId)}`),
    create: (data: unknown) => request<any>('/apps', { method: 'POST', body: JSON.stringify(data) }),
    linkRegistryClient: (data: {
      client_id: string
      partner_id?: number | null
      environment_id: number
      name: string
      is_sandbox?: boolean
      free_app?: boolean
    }) => request<any>('/apps/link-registry-client', { method: 'POST', body: JSON.stringify(data) }),
    generateCredentials: () => request<{ client_id: string; client_secret: string }>('/apps/generate-credentials'),
    update: (id: number, data: unknown) => request(`/apps/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/apps/${id}`, { method: 'DELETE' }),
    deleteApp: (id: number) => request<{ change_request: any; message: string }>(`/apps/${id}`, { method: 'DELETE' }),
    deleteRegistryApp: (clientKey: string, project: string) => request<{ change_request: any; message: string }>(`/apps/registry/${encodeURIComponent(clientKey)}?project=${encodeURIComponent(project)}`, { method: 'DELETE' }),
    mine: () => request<{ apps: any[]; count: number }>('/apps/mine'),
    mineDetail: (clientId: string) => request<any>(`/apps/mine/${encodeURIComponent(clientId)}`),
    uploadIcon: (id: number, base64DataUrl: string) =>
      request(`/apps/${id}/icon`, { method: 'PUT', body: JSON.stringify({ icon: base64DataUrl }) }),
    deleteIcon: (id: number) =>
      request(`/apps/${id}/icon`, { method: 'DELETE' }),
    updateMine: (clientId: string, data: { name?: string; description?: string }) =>
      request(`/apps/mine/${encodeURIComponent(clientId)}`, { method: 'PUT', body: JSON.stringify(data) }),
    uploadMineIcon: (clientId: string, icon: string) =>
      request(`/apps/mine/${encodeURIComponent(clientId)}/icon`, { method: 'PUT', body: JSON.stringify({ icon }) }),
    deleteMineIcon: (clientId: string) =>
      request(`/apps/mine/${encodeURIComponent(clientId)}/icon`, { method: 'DELETE' }),
  },

  // Datastore
  datastore: {
    clients: (partnerId: number, environmentId?: number) => {
      const q = environmentId ? `?environment_id=${environmentId}` : ''
      return request(`/datastore/partners/${partnerId}/clients${q}`)
    },
    appStatus: (projectId: string, region: string, keyName: string) =>
      request(`/datastore/app-status/${encodeURIComponent(projectId)}/${encodeURIComponent(region)}/${encodeURIComponent(keyName)}`),
    streamingUsers: (partnerId: number, environmentId?: number) => {
      const q = environmentId ? `?environment_id=${environmentId}` : ''
      return request(`/datastore/partners/${partnerId}/streaming-users${q}`)
    },
    seedAllApps: (envId: number) => request(`/datastore/environments/${envId}/seed-partners`, { method: 'POST' }),
    analytics: (partnerId: number, days: number = 7, appId?: number) => {
      const params = new URLSearchParams({ days: String(days) })
      if (appId) params.set('app_id', String(appId))
      return request(`/datastore/partners/${partnerId}/analytics?${params}`)
    },
    refreshAnalytics: (partnerId: number, appId: number, days: number = 7, refreshDays: number = 2) => {
      const params = new URLSearchParams({ days: String(days), app_id: String(appId), refresh_days: String(refreshDays) })
      return request(`/datastore/partners/${partnerId}/analytics/refresh?${params}`, { method: 'POST' })
    },
    config: {
      get: (projectId: string, keyName: string, kind?: string) => {
        const q = kind ? `?kind=${encodeURIComponent(kind)}` : ''
        return request(`/datastore/config/${encodeURIComponent(projectId)}/entity/${encodeURIComponent(keyName)}${q}`)
      },
      update: (projectId: string, keyName: string, data: { properties: Record<string, any>; environment_id?: number; partner_id?: number }, kind?: string) => {
        const q = kind ? `?kind=${encodeURIComponent(kind)}` : ''
        return request(`/datastore/config/${encodeURIComponent(projectId)}/entity/${encodeURIComponent(keyName)}${q}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        })
      },
    },
    developerClients: () => request<{ clients: any[]; stats: { total: number; realtime: number; projects: number }; stale: boolean; last_synced: string | null; errors: string[] }>('/datastore/developer-clients'),
    developerClient: (projectId: string, clientKey: string) =>
      request<{ client: any | null }>(`/datastore/developer-client/${encodeURIComponent(projectId)}/${encodeURIComponent(clientKey)}`),
    developerClientById: (clientId: string) =>
      request<{ client: any | null }>(`/datastore/developer-client-by-id/${encodeURIComponent(clientId)}`),
    developerClientAnalytics: (clientId: string, days: number = 7) =>
      request<any>(`/datastore/developer-client-analytics/${encodeURIComponent(clientId)}?days=${days}`),
    refreshDeveloperClientAnalytics: (clientId: string, days: number = 7, refreshDays: number = 2) =>
      request<any>(`/datastore/developer-client-analytics/${encodeURIComponent(clientId)}/refresh?days=${days}&refresh_days=${refreshDays}`, { method: 'POST' }),
    updateDeveloperClient: (clientId: string, data: Record<string, any>) =>
      request<{ success: boolean; warnings?: string[] }>(`/datastore/developer-client/${encodeURIComponent(clientId)}`, { method: 'PUT', body: JSON.stringify(data) }),
    getDeveloperClientAuditLog: (clientId: string) =>
      request<{ logs: Array<{ clientKey: string; createdAt: string; log: string; updatedBy: string }> }>(`/datastore/developer-client/${encodeURIComponent(clientId)}/audit-log`),
    uploadDeveloperClientIcon: (clientId: string, base64DataUrl: string) =>
      request<{ success: boolean }>(`/datastore/developer-client/${encodeURIComponent(clientId)}/icon`, { method: 'PUT', body: JSON.stringify({ icon: base64DataUrl }) }),
    deleteDeveloperClientIcon: (clientId: string) =>
      request<{ success: boolean }>(`/datastore/developer-client/${encodeURIComponent(clientId)}/icon`, { method: 'DELETE' }),
    refreshDeveloperClients: () => request<{ projects_synced: number; entries_upserted: number; entries_deleted: number; errors: string[]; reauth_required?: boolean }>('/datastore/developer-clients/refresh', { method: 'POST' }),
    gallery: () => request<{ apps: any[]; count: number }>('/datastore/gallery'),
    // New standalone gallery API (works for both OAuth-linked and portal/realtime apps)
    galleryForApp: (clientId: string) =>
      request<{ gallery: { clientId: string; iconUrl: string | null; galleryEnabled: boolean; galleryDescription: string | null; galleryDisplayName: string | null; galleryCategory: string | null; galleryScreenshots: string[]; websiteUrl: string | null; appStoreUrl: string | null; playStoreUrl: string | null } }>(
        `/datastore/gallery/app/${encodeURIComponent(clientId)}`
      ),
    updateGalleryForApp: (clientId: string, data: Record<string, any>) =>
      request<{ success: boolean }>(`/datastore/gallery/app/${encodeURIComponent(clientId)}`, { method: 'PUT', body: JSON.stringify(data) }),
    uploadGalleryIcon: (clientId: string, base64DataUrl: string) =>
      request<{ success: boolean }>(`/datastore/gallery/app/${encodeURIComponent(clientId)}/icon`, { method: 'PUT', body: JSON.stringify({ icon: base64DataUrl }) }),
    deleteGalleryIcon: (clientId: string) =>
      request<{ success: boolean }>(`/datastore/gallery/app/${encodeURIComponent(clientId)}/icon`, { method: 'DELETE' }),
    discoverAppMetadata: (url: string) =>
      request<{ platform: 'apple' | 'google'; name: string; description: string; category: string; iconDataUri: string | null; screenshotDataUris: string[]; warning?: string; error?: string }>(
        '/datastore/discover-app-metadata',
        { method: 'POST', body: JSON.stringify({ url }) }
      ),
  },

  // Spanner entity configuration (read-only)
  spanner: {
    entityConfig: (clientId: string, projectId: string) =>
      request(`/spanner/entity-config/${encodeURIComponent(clientId)}?project_id=${encodeURIComponent(projectId)}`),
    entityConfigDiff: () => request('/spanner/entity-config-diff'),
    sourceStreams: {
      list: () => request('/spanner/source-streams'),
      create: (data: { name: string; numeric_id?: number | null; description: string; category?: string }) =>
        request('/spanner/source-streams', { method: 'POST', body: JSON.stringify(data) }),
      update: (name: string, data: { numeric_id?: number | null; description?: string; category?: string }) =>
        request(`/spanner/source-streams/${encodeURIComponent(name)}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (name: string) =>
        request(`/spanner/source-streams/${encodeURIComponent(name)}`, { method: 'DELETE' }),
    },
  },

  // App Integrations
  appIntegrations: {
    list: (params?: Record<string, string>) => {
      const q = params ? '?' + new URLSearchParams(params).toString() : ''
      return request(`/app-integrations${q}`)
    },
    get: (id: number) => request(`/app-integrations/${id}`),
    create: (data: unknown) => request('/app-integrations', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: unknown) => request(`/app-integrations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/app-integrations/${id}`, { method: 'DELETE' }),
    environments: () => request<any[]>('/app-integrations/environments'),
    users: {
      list: (appId: number) => request(`/app-integrations/${appId}/users`),
      create: (appId: number, data: unknown) => request(`/app-integrations/${appId}/users`, { method: 'POST', body: JSON.stringify(data) }),
      update: (appId: number, userId: number, data: unknown) => request(`/app-integrations/${appId}/users/${userId}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (appId: number, userId: number) => request(`/app-integrations/${appId}/users/${userId}`, { method: 'DELETE' }),
    },
  },

  // BigQuery Message Analytics
  bigquery: {
    overview: () => request('/bigquery/overview'),
  },

  // ---------------------------------------------------------------
  // Provisioner: catalog CRUD + worker health proxy. Mirrors the
  // backend routes in routes/provisioner.ts. Mutations are
  // dexcom_admin only on the server; the UI hides the buttons
  // for non-admins but the server is always the source of truth.
  // ---------------------------------------------------------------
  provisioner: {
    health: () => request<{ reachable: boolean; url: string; status?: string; version?: string; error?: string }>('/provisioner/health'),
    reloadCatalog: () => request<{ envs?: number; clients?: number; ok?: boolean }>('/provisioner/catalog/reload', { method: 'POST' }),
    syncDeveloperNames: () => request<{ projects_queried: number; total_developer_clients: number; apps_updated: number; apps_skipped: number; errors: string[] }>('/provisioner/sync-developer-names', { method: 'POST' }),
    envs: {
      list: () => request<import('../types').ProvisionerEnv[]>('/provisioner/envs'),
      /**
       * Bulk per-env live Datastore count. Used by the Partners tab on
       * mount to populate the sidebar counts in one round-trip rather
       * than 16 sequential clicks. Per-env failures are returned in
       * `counts[i].error` rather than failing the whole call. Source
       * is `'live'` (fresh worker fetch), `'cache'` (DB-cached, may be
       * stale or no-token fallback), or `'error'`.
       */
      liveCounts: (opts?: { fresh?: boolean }) => {
        const qs = opts?.fresh ? '?fresh=1' : ''
        return request<{
          counts: Array<{
            env_id: number
            env_name: string
            active: number | null
            deleted: number | null
            total: number | null
            fetched_at: string
            source: 'live' | 'cache' | 'error'
            error?: string
          }>
          _cache: { fetched_at: string }
        }>(`/provisioner/envs/live-counts${qs}`)
      },
      get: (id: number) => request<import('../types').ProvisionerEnv>(`/provisioner/envs/${id}`),
      create: (data: Partial<import('../types').ProvisionerEnv>) =>
        request<import('../types').ProvisionerEnv>('/provisioner/envs', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: number, data: Partial<import('../types').ProvisionerEnv>) =>
        request<import('../types').ProvisionerEnv>(`/provisioner/envs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: number) => request(`/provisioner/envs/${id}`, { method: 'DELETE' }),
    },
    clients: {
      // Server filters: env_id (preferred), env_name (legacy), q (free text), enabled.
      list: (params?: { env_id?: number; env_name?: string; q?: string; enabled?: boolean }) => {
        const sp = new URLSearchParams()
        if (params?.env_id != null) sp.set('env_id', String(params.env_id))
        if (params?.env_name) sp.set('env_name', params.env_name)
        if (params?.q) sp.set('q', params.q)
        if (params?.enabled != null) sp.set('enabled', params.enabled ? 'true' : 'false')
        const q = sp.toString()
        return request<import('../types').ProvisionerClient[]>(`/provisioner/clients${q ? `?${q}` : ''}`)
      },
      create: (data: Partial<import('../types').ProvisionerClient>) =>
        request<import('../types').ProvisionerClient>('/provisioner/clients', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: number, data: Partial<import('../types').ProvisionerClient>) =>
        request<import('../types').ProvisionerClient>(`/provisioner/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: number) => request(`/provisioner/clients/${id}`, { method: 'DELETE' }),
      // Bulk CSV import — body is `{ env_id, csv, replace? }`. The server
      // upserts on (env_id, client_id); replace=true wipes the env first.
      import: (env_id: number, csv: string, replace = false) =>
        request<{ inserted: number; updated: number; total: number; replace: boolean }>('/provisioner/clients/import', {
          method: 'POST',
          body: JSON.stringify({ env_id, csv, replace }),
        }),
    },
    releaseTemplate: {
      // Backend stores the template as a free-form string in the settings
      // table under the key 'provisioner.release_template'. Returns
      // { value, updated_at }; PUT body is { value }.
      get: () => request<{ value: string; updated_at: string | null }>('/provisioner/release-template'),
      update: (value: string) =>
        request('/provisioner/release-template', { method: 'PUT', body: JSON.stringify({ value }) }),
    },
    // -----------------------------------------------------------------
    // Live partner / client surface — backed by the Go worker's
    // Datastore + Dataflow probe. Used by the Partners tab in the UI.
    // -----------------------------------------------------------------
    partners: {
      /** GET /api/provisioner/envs/:id/partners — live + catalog merged.
       *  Backed by a 5-minute DB cache on the server; pass fresh=true
       *  to bypass and force a Datastore re-read. */
      listForEnv: (envId: number, opts?: { includeDeleted?: boolean; fresh?: boolean }) => {
        const sp = new URLSearchParams()
        if (opts?.includeDeleted) sp.set('include_deleted', '1')
        if (opts?.fresh) sp.set('fresh', '1')
        const q = sp.toString()
        return request<import('../types').ProvisionerEnvPartnersResponse>(
          `/provisioner/envs/${envId}/partners${q ? `?${q}` : ''}`,
        )
      },
      /** POST /api/provisioner/envs/:id/refresh-cells — calls Dataflow. mgmt+. */
      refreshCells: (envId: number) =>
        request<import('../types').ProvisionerRefreshCellsResponse>(
          `/provisioner/envs/${envId}/refresh-cells`,
          { method: 'POST' },
        ),
      /** POST /api/provisioner/partners — admin-only worker-side add. */
      add: (envId: number, clientID: string, displayName?: string) =>
        request<{ client_id: string; display_name: string }>(
          '/provisioner/partners',
          {
            method: 'POST',
            body: JSON.stringify({
              env_id: envId,
              client_id: clientID,
              display_name: displayName,
            }),
          },
        ),
      /** GET /api/provisioner/partners/:client_id — cross-env summary. */
      summary: (clientID: string) =>
        request<{
          client_id: string
          environments: Array<{
            environment: string
            in_catalog: boolean
            display_name?: string
            cell?: Record<string, unknown>
          }>
          recent_deployments: Array<Record<string, unknown>>
        }>(`/provisioner/partners/${encodeURIComponent(clientID)}`),
      /** GET /api/provisioner/partners/:client_id/config?env_id= — masked entity. mgmt+. */
      config: (clientID: string, envId: number) =>
        request<Record<string, unknown>>(
          `/provisioner/partners/${encodeURIComponent(clientID)}/config?env_id=${envId}`,
        ),
      onboarding: {
        start: (data: {
          client_id: string
          environment_id: number
          properties: Record<string, unknown>
          commit_hash_override?: string
          credentials?: Record<string, string>
        }) => request<{ run_id: number; status: string }>('/provisioner/onboarding', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
        get: (runId: number) => request<{
          id: number
          client_id: string
          environment: string
          status: string
          commit_hash: string
          commit_hash_source: string
          message_id?: string | null
          client_job_id?: string | null
          dataflow_region?: string | null
          error_message?: string | null
          events: Array<{
            id: number
            event_type: string
            status: string
            message: string
            details: Record<string, unknown>
            created_at: string
          }>
        }>(`/provisioner/onboarding/${runId}`),
        cancel: (runId: number) => request(`/provisioner/onboarding/${runId}/cancel`, { method: 'POST' }),
        latest: (clientId: string, environmentId: number) =>
          request<any>(`/provisioner/onboarding/latest?client_id=${encodeURIComponent(clientId)}&environment_id=${environmentId}`),
        state: (clientId: string, environmentId: number) =>
          request<{
            entity_exists: boolean
            client_job_id: string | null
            job_state: string | null
            commit_hash: string | null
            client_push_url: string
            datastore_client_key?: string
            credentials: null
              | { authScheme: 'client_credentials'; client_id: string; scope: string; token_url: string; has_secret: boolean }
              | { authScheme: 'Basic'; username: string; has_secret: boolean }
          }>(`/provisioner/onboarding/state?client_id=${encodeURIComponent(clientId)}&environment_id=${environmentId}`),
        testToken: (clientId: string, environmentId: number, credentials: Record<string, string>) =>
          request<{
            ok: boolean
            status: number | null
            elapsed_ms?: number
            token_type?: string | null
            expires_in?: number | null
            scope?: string | null
            error?: string
          }>('/provisioner/onboarding/test-token', {
            method: 'POST',
            body: JSON.stringify({ client_id: clientId, environment_id: environmentId, credentials }),
          }),
        adopt: (clientId: string, environmentId: number) =>
          request<{ run_id: number; status: string }>('/provisioner/onboarding/adopt', {
            method: 'POST',
            body: JSON.stringify({ client_id: clientId, environment_id: environmentId }),
          }),
        deprovision: (clientId: string, environmentId: number) =>
          request<{ success: boolean; drained_job_id: string | null }>('/provisioner/onboarding/deprovision', {
            method: 'POST',
            body: JSON.stringify({ client_id: clientId, environment_id: environmentId }),
          }),
      },
      /** POST /api/provisioner/envs/:id/adopt-drift — admin-only.
       *  Promotes a Live-Only drift row (Datastore client_id with no
       *  catalog entry) into the catalog. Always UPSERTs a
       *  provisioner_clients row per env in scope (the only writes
       *  needed to clear drift). Optionally also creates a portal
       *  partner + apps row + per-env partner_environments rows when
       *  the caller passes create_partner / partner_id and create_app.
       *
       *  Multi-env: a single OAuth client_id may legitimately run in
       *  several GCP projects. The URL `envId` names the *primary*
       *  env (the one the user clicked Adopt on); pass
       *  `additional_env_ids` to also adopt the same client_id in
       *  peer envs in one transaction. provisioner_clients UPSERT
       *  makes re-clicking Adopt idempotent. apps.client_id is
        *  globally UNIQUE so exactly ONE apps row is created (under
        *  the primary env's portal `environments` row); per-env
        *  Dataflow project mappings go into partner_environments,
        *  which has UNIQUE (partner_id, environment_id).
       *
       *  Returns the inserted rows plus a `warnings[]` for non-fatal
        *  issues (e.g. apps.client_id collision under a different
        *  partner, no portal environment matched a provisioner env's
        *  gcp_project_id). */
      adoptDrift: (
        envId: number,
        body: {
          client_id: string
          partner_name?: string | null
          is_sandbox?: boolean
          create_partner?: { display_name: string; contact_email?: string | null }
          partner_id?: number
          create_app?: boolean
          app_name?: string
          /** Peer environments to also adopt in. UI populates
           *  this from envs already loaded in the list view that
           *  show the same client_id in their `live_only` drift.
           *  Server de-dupes against envId. */
          additional_env_ids?: number[]
        },
      ) =>
        request<{
          provisioner_clients: Record<string, unknown>[]
          partner: Record<string, unknown> | null
          app: Record<string, unknown> | null
          partner_environments: Record<string, unknown>[]
          env_ids: number[]
          warnings: string[]
        }>(`/provisioner/envs/${envId}/adopt-drift`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
    },
  },

  // ---------------------------------------------------------------
  // Releases: full lifecycle. preview() returns a matrix; the UI
  // builder lets the operator deselect rows then calls submit() to
  // persist the release in pending_approval. approve() requires
  // 10-char note + full SHA + matching plan_id (409 on drift).
  // ---------------------------------------------------------------
  releases: {
    list: (params?: Record<string, string>) => {
      const q = params ? '?' + new URLSearchParams(params).toString() : ''
      return request<import('../types').Release[]>(`/releases${q}`)
    },
    get: (id: number) => request<import('../types').Release>(`/releases/${id}`),
    pendingApprovalCount: () => request<{ count: number }>('/releases/_meta/pending-approval-count'),

    /** Latest completed/soaking release for a given scope (prod, non-dev, sandbox, or all) */
    latest: (scope?: 'prod' | 'non-dev' | 'sandbox' | 'all') =>
      request<{
        latest: {
          id: number
          target_sha: string
          target_sha7: string
          release_tag: string | null
          scope: string
          status: string
          created_at: string
          approved_at: string | null
          created_by_name: string
          approved_by_name: string | null
        } | null
      }>(`/releases/_meta/latest${scope ? `?scope=${scope}` : ''}`),

    /** Fleet status: per-environment compliance & health for prod environments */
    fleetStatus: () =>
      request<{
        latest_release: {
          id: number
          target_sha7: string
          release_tag: string | null
          status: string
          approved_at: string | null
          created_at: string
        } | null
        target_sha7: string | null
        environments: Array<{
          id: number
          name: string
          display_name: string
          region: string
          tier: string
          total_partners: number
          on_target: number
          pct_compliant: number
          health: {
            overall: 'healthy' | 'degraded' | 'failed'
            healthy: number
            degraded: number
            failed: number
            deploying: number
            draining: number
            not_deployed: number
            unknown: number
          }
          last_synced: string | null
        }>
      }>('/releases/_meta/fleet-status'),

    // Pre-flight check: does the realtime push Dataflow template artifact
    // exist in GCS for the given release tag or target SHA? Used by the
    // Release Builder form to surface bad refs (typos, unbuilt tags)
    // before preview / submit.
    checkArtifact: (ref: string) =>
      request<{
        exists: boolean
        ref: string
        bucket: string
        object: string
      }>(`/releases/check-artifact?ref=${encodeURIComponent(ref)}`),

    preview: (data: {
      target_sha: string
      release_tag?: string
      scope?: 'prod' | 'non-dev' | 'all' | 'sandbox'
      /**
       * Explicit env stages from the wizard picker. When non-empty the
       * worker ignores `scope` and expands these envs directly.
       */
      stages?: Array<{ name: string; envs: string[]; client_ids?: string[] }>
    }) =>
      request<import('../types').PreviewMatrix>('/releases/preview', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    submit: (data: {
      target_sha: string
      release_tag?: string
      scope?: 'prod' | 'non-dev' | 'all' | 'sandbox'
      plan_id: string
      partners: import('../types').ReleasePartner[]
    }) =>
      request<import('../types').Release>('/releases', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    approve: (id: number, data: { approval_note: string; full_sha_typed: string; plan_id: string }) =>
      request<import('../types').Release>(`/releases/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    cancel: (id: number) =>
      request<import('../types').Release>(`/releases/${id}/cancel`, { method: 'POST' }),

    redispatch: (id: number) =>
      request<import('../types').Release>(`/releases/${id}/redispatch`, { method: 'POST' }),

    rolloutProgress: (releaseId: number, rolloutId: number) =>
      request(`/releases/${releaseId}/rollouts/${rolloutId}/progress`),

    pipelineStatus: (releaseId: number) =>
      request<import('../types').PipelineStatus>(`/releases/${releaseId}/pipeline-status`),

    drainStep: (releaseId: number, rolloutId: number, stepId: number) =>
      request<{ success: boolean; drain_status: string; message: string }>(
        `/releases/${releaseId}/rollouts/${rolloutId}/steps/${stepId}/drain`,
        { method: 'POST' },
      ),

    activity: (releaseId: number, sinceId?: number) =>
      request<{
        rollout_id: number | null;
        events: Array<{
          id: number;
          step_id: number | null;
          environment: string | null;
          client_id: string | null;
          ts: string;
          event_type: string;
          message: string;
          level: 'info' | 'success' | 'warn' | 'error';
        }>;
      }>(
        `/releases/${releaseId}/activity${sinceId ? `?since=${sinceId}` : ''}`,
      ),
  },
}

export function chatStream(
  message: string,
  conversationId: number | null,
  model: string,
  onDelta: (content: string) => void,
  onToolCall: (name: string, args: unknown) => void,
  onToolResult: (name: string, result: unknown) => void,
  onDone: (conversationId: number) => void,
  onError: (message: string) => void
): () => void {
  const token = getToken()
  const controller = new AbortController()

  fetch(`${API_BASE}/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ message, conversation_id: conversationId, model }),
    signal: controller.signal,
  }).then(async (res) => {
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Unknown error' }))
      onError(err.error || 'Chat failed')
      return
    }

    const reader = res.body!.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      let currentEvent = ''
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim()
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6))
            if (currentEvent === 'delta') onDelta(data.content)
            else if (currentEvent === 'tool_call') onToolCall(data.name, data.arguments)
            else if (currentEvent === 'tool_result') onToolResult(data.name, data.result)
            else if (currentEvent === 'done') onDone(data.conversation_id)
            else if (currentEvent === 'error') onError(data.message)
          } catch { /* ignore */ }
        }
      }
    }
  }).catch((err) => {
    if (err.name !== 'AbortError') onError(String(err))
  })

  return () => controller.abort()
}
